import { useCallback, useEffect, useState, type ReactNode } from "react";

import { API_ERROR_BUS } from "@/utils/eventBus";

import { ToastContext, type ToastType } from "./ToastContext";

interface ToastItem {
  readonly id: number;
  readonly type: ToastType;
  readonly message: string;
}

const AUTO_DISMISS_MS = 4000;

let nextId = 0;

export function ToastProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([]);

  const toast = useCallback((type: ToastType, message: string): void => {
    const id = ++nextId;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, AUTO_DISMISS_MS);
  }, []);

  const dismiss = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    return API_ERROR_BUS.subscribe((event) => {
      const status = event.status ?? 0;
      if (status >= 500 || status === 0) {
        toast("error", event.message);
      }
    });
  }, [toast]);

  const icons: Record<ToastType, string> = {
    success: "✓",
    error: "!",
    warning: "!",
    info: "i",
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="toast-container" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast--${t.type}`} role="status">
            <span className="toast__icon" aria-hidden>
              {icons[t.type]}
            </span>
            <span className="toast__message">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
